// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's Etage editor (frontend-u0o.7, slice S7; user
 * 2026-11-04: "a way to define/change our floors if that fails
 * automatically"): the storey rows with what stands on each, and the
 * `POST /model/edits` ops for renaming one, setting its floor and ceiling
 * (taking along the walls, rooms and floor slabs that stand on them),
 * adding one on top, deleting one (ruxd refuses while elements remain —
 * its reason in Danish, and the reassign that clears it), splitting one at
 * a height, merging two neighbours, moving selected elements to a storey
 * and marking the ground storey (`is_ground`, ReUseX schema v44). World
 * heights, as `GET /storeys` serves them. Pure: no React, no DOM.
 */

import type { ModelEditOp, Storey } from '../api/types';
import { findElement, type GeometriModel } from './geometri';
import { storeyShortLabel } from './storeyPicker';

/** "±0,00", "+3,00", "−0,30" (the level of a height, as a plan reads it). */
export function levelText(z: number): string {
  if (Math.abs(z) < 0.005) return '±0,00';
  return `${z > 0 ? '+' : '−'}${Math.abs(z).toFixed(2).replace('.', ',')}`;
}

const metres = (v: number) => `${v.toFixed(2).replace('.', ',')} m`;
const round = (v: number) => Math.round(v * 1e6) / 1e6;

/** Heights within this of a storey's floor or ceiling stand on it (metres). */
const ON_LEVEL = 0.05;
/** A storey is at least this high. */
const MIN_HEIGHT = 0.5;
/** A storey's band may reach this far into its neighbour's (detected bands touch). */
const OVERLAP = 0.05;
/** A split lies at least this far from the floor and the ceiling (ReUseX storey.split). */
const SPLIT_MARGIN = 0.1;
/** A new storey's height when there is none to copy. */
const DEFAULT_HEIGHT = 2.8;
/** The gap under a new storey when there is none to copy (a floor slab). */
const DEFAULT_GAP = 0.2;

/**
 * The ground (entrance) storey's id: the one marked `is_ground`, else the
 * one whose floor is nearest world z = 0 (where the scan started, almost
 * always the entrance); null without storeys.
 */
export function groundStoreyId(storeys: readonly Storey[] | null | undefined): number | null {
  const list = storeys ?? [];
  const marked = list.find((s) => s.is_ground === true);
  if (marked) return marked.id;
  let best: Storey | null = null;
  for (const s of list) if (Number.isFinite(s.z_min) && (best === null || Math.abs(s.z_min) < Math.abs(best.z_min))) best = s;
  return best?.id ?? null;
}

export interface StoreyRow {
  guid: string;
  id: number;
  name: string;
  zMin: number;
  zMax: number;
  height: number;
  /** Marked as the ground storey (`is_ground`). */
  isGround: boolean;
  /** The picker's label: "S", "1", "-1" (from the ground storey). */
  label: string;
  source: Storey['source'];
  edited: boolean;
  counts: { walls: number; openings: number; spaces: number; slabs: number };
}

/** Lowest first, by floor height then id (the order the storeys stand in). */
const lowestFirst = (storeys: readonly Storey[]) => [...storeys].sort((a, b) => a.z_min - b.z_min || a.id - b.id);

/** The Etage editor's rows, top storey first (as a section reads). */
export function storeyRows(model: GeometriModel): StoreyRow[] {
  const storeys = lowestFirst(model.storeys ?? []);
  const groundId = groundStoreyId(storeys);
  const gi = storeys.findIndex((s) => s.id === groundId);
  const hostStorey = new Map((model.walls ?? []).map((w) => [w.guid, w.storey_id]));
  const count = (id: number) => ({
    walls: (model.walls ?? []).filter((w) => w.storey_id === id).length,
    openings: (model.openings ?? []).filter((o) => (o.storey_id ?? hostStorey.get(o.wall_guid) ?? null) === id).length,
    spaces: (model.spaces ?? []).filter((s) => s.storey_id === id).length,
    slabs: (model.slabs ?? []).filter((s) => s.storey_id === id).length,
  });
  return storeys
    .map((s, i) => ({
      guid: s.guid,
      id: s.id,
      name: s.name,
      zMin: s.z_min,
      zMax: s.z_max,
      height: round(s.z_max - s.z_min),
      isGround: s.is_ground === true,
      label: storeyShortLabel(i - (gi < 0 ? 0 : gi)),
      source: s.source,
      edited: s.edited === true,
      counts: count(s.id),
    }))
    .reverse();
}

function byGuid(model: GeometriModel, guid: string): Storey | null {
  return model.storeys?.find((s) => s.guid === guid) ?? null;
}

function byId(model: GeometriModel, id: number): Storey | null {
  return model.storeys?.find((s) => s.id === id) ?? null;
}

/** Rename; null for an empty or unchanged name. */
export function storeyNameOps(model: GeometriModel, guid: string, name: string): ModelEditOp[] | null {
  const s = byGuid(model, guid);
  const n = name.trim();
  if (!s || n === '' || n === s.name) return null;
  return [{ op: 'storey.update', guid, set: { name: n } }];
}

export type StoreyResult = { ops: ModelEditOp[]; label: string } | { error: string };

/**
 * Set a storey's floor (`zMin`) and / or ceiling (`zMax`), world. With
 * `carry`, what stands on the old floor or reaches the old ceiling moves
 * with it: walls' bases and tops, rooms' floors and flat ceilings, and the
 * floor slabs whose top is the floor (their thickness kept). Refused for a
 * storey lower than half a metre, or one that runs into its neighbour.
 */
export function storeyHeightOps(model: GeometriModel, guid: string, heights: { zMin?: number; zMax?: number }, carry: boolean): StoreyResult {
  const s = byGuid(model, guid);
  if (!s) return { error: 'Etagen findes ikke længere' };
  const zMin = round(heights.zMin ?? s.z_min);
  const zMax = round(heights.zMax ?? s.z_max);
  if (!Number.isFinite(zMin) || !Number.isFinite(zMax)) return { error: 'Skriv en højde i meter' };
  if (Math.abs(zMin - s.z_min) < 5e-4 && Math.abs(zMax - s.z_max) < 5e-4) return { error: 'Højderne er de samme' };
  if (zMax - zMin < MIN_HEIGHT) return { error: `Etagen skal være mindst ${metres(MIN_HEIGHT)} høj` };
  const sorted = lowestFirst(model.storeys ?? []);
  const at = sorted.findIndex((r) => r.guid === guid);
  const below = sorted[at - 1];
  const above = sorted[at + 1];
  if (above && Math.abs(zMax - s.z_max) >= 5e-4 && zMax > above.z_min + OVERLAP) return { error: `Loftet går op i ${above.name} (gulv ${levelText(above.z_min)})` };
  if (below && Math.abs(zMin - s.z_min) >= 5e-4 && zMin < below.z_max - OVERLAP) return { error: `Gulvet går ned i ${below.name} (loft ${levelText(below.z_max)})` };

  // Only what moved (a stored height is not rounded: compare to the half millimetre).
  const set: Record<string, number> = {};
  if (Math.abs(zMin - s.z_min) >= 5e-4) set.z_min = zMin;
  if (Math.abs(zMax - s.z_max) >= 5e-4) set.z_max = zMax;
  const ops: ModelEditOp[] = [{ op: 'storey.update', guid, set }];
  if (carry) {
    const onFloor = (z: number) => 'z_min' in set && Math.abs(z - s.z_min) <= ON_LEVEL;
    const atCeiling = (z: number) => 'z_max' in set && Math.abs(z - s.z_max) <= ON_LEVEL;
    for (const w of model.walls ?? []) {
      if (w.storey_id !== s.id) continue;
      const b = onFloor(w.z_base);
      const t = atCeiling(w.z_top);
      if (b || t) ops.push({ op: 'wall.update', guid: w.guid, set: { z_base: b ? zMin : w.z_base, z_top: t ? zMax : w.z_top } });
    }
    for (const sp of model.spaces ?? []) {
      if (sp.storey_id !== s.id) continue;
      const patch: Record<string, number> = {};
      if (onFloor(sp.z_floor)) patch.z_floor = zMin;
      if (atCeiling(sp.z_ceiling) && (sp.ceiling_planes ?? []).length === 0) patch.z_ceiling = zMax;
      if (Object.keys(patch).length > 0) ops.push({ op: 'space.update', guid: sp.guid, set: patch });
    }
    for (const sl of model.slabs ?? []) {
      if (sl.storey_id !== s.id || sl.source === undefined || sl.kind !== 'floor' || !onFloor(sl.z_top)) continue;
      const dz = zMin - sl.z_top;
      ops.push({ op: 'slab.update', guid: sl.guid, set: { z_top: zMin, z_base: round(sl.z_base + dz) } });
    }
  }
  return { ops, label: `Etagehøjder · ${s.name}` };
}

/** "2. sal": the next storey up, counted from the ground storey; "Stueetage" for the first. */
export function nextStoreyName(model: GeometriModel): string {
  const storeys = lowestFirst(model.storeys ?? []);
  if (storeys.length === 0) return 'Stueetage';
  const gi = Math.max(0, storeys.findIndex((s) => s.id === groundStoreyId(storeys)));
  const n = storeys.length - gi;
  const name = n <= 0 ? `${storeys.length + 1}. etage` : `${n}. sal`;
  return storeys.some((s) => s.name === name) ? `Etage ${storeys.length + 1}` : name;
}

/**
 * A new storey on top of the building (idx after the highest): after the
 * same gap as the one under the top storey, as high as it (2,80 m and a
 * 20 cm gap when there is nothing to copy). A building without storeys
 * gets "Stueetage" at ±0. Storeys in between come from Del (a split).
 */
export function addStoreyOps(model: GeometriModel, guid: string, name?: string): StoreyResult {
  const storeys = lowestFirst(model.storeys ?? []);
  const label = (n: string) => `Ny etage · ${n}`;
  if (storeys.length === 0) {
    const n = name?.trim() || 'Stueetage';
    return { ops: [{ op: 'storey.create', record: { guid, idx: 0, name: n, z_min: 0, z_max: DEFAULT_HEIGHT } }], label: label(n) };
  }
  const top = storeys[storeys.length - 1];
  const under = storeys[storeys.length - 2];
  const gap = under ? Math.max(0, top.z_min - under.z_max) : DEFAULT_GAP;
  const height = top.z_max - top.z_min > MIN_HEIGHT ? top.z_max - top.z_min : DEFAULT_HEIGHT;
  const zMin = round(top.z_max + gap);
  const n = name?.trim() || nextStoreyName(model);
  const idx = Math.max(...storeys.map((s) => s.id)) + 1;
  return { ops: [{ op: 'storey.create', record: { guid, idx, name: n, z_min: zMin, z_max: round(zMin + height) } }], label: label(n) };
}

/** Delete a storey: ruxd refuses (400) while walls, rooms or slabs stand on it (`storeyDeleteRefusal`). */
export function deleteStoreyOps(model: GeometriModel, guid: string): ModelEditOp[] {
  return byGuid(model, guid) ? [{ op: 'storey.delete', guid }] : [];
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The server's refusal of a storey delete in Danish ("Etagen har stadig 12
 * vægge og 4 rum — …"); any other message as it came.
 */
export function storeyDeleteRefusal(message: string): string {
  if (!/still holds/.test(message)) return message;
  const count = (what: string) => Number(new RegExp(`(\\d+) ${what}\\(s\\)`).exec(message)?.[1] ?? 0);
  const parts = [
    [count('wall'), 'væg', 'vægge'],
    [count('space'), 'rum', 'rum'],
    [count('slab'), 'dæk', 'dæk'],
    [count('stair'), 'trappe', 'trapper'],
  ]
    .filter(([n]) => (n as number) > 0)
    .map(([n, one, many]) => plural(n as number, one as string, many as string));
  const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} og ${parts[parts.length - 1]}` : (parts[0] ?? 'elementer');
  return `Etagen har stadig ${list} — flyt dem til en anden etage, eller slet dem først.`;
}

/**
 * Everything on storey `fromGuid` moved to storey `toId` (walls, rooms,
 * slabs — openings follow their host wall; each keeps its world position),
 * then, with `andDelete`, the storey deleted: one batch, one undo step.
 */
export function reassignAllOps(model: GeometriModel, fromGuid: string, toId: number, andDelete: boolean): ModelEditOp[] {
  const s = byGuid(model, fromGuid);
  if (!s) return [];
  const ops: ModelEditOp[] = [];
  for (const w of model.walls ?? []) if (w.storey_id === s.id) ops.push({ op: 'wall.update', guid: w.guid, set: { storey_id: toId } });
  for (const sp of model.spaces ?? []) if (sp.storey_id === s.id) ops.push({ op: 'space.update', guid: sp.guid, set: { storey_id: toId } });
  for (const sl of model.slabs ?? []) if (sl.storey_id === s.id && sl.source !== undefined) ops.push({ op: 'slab.update', guid: sl.guid, set: { storey_id: toId } });
  if (andDelete) ops.push({ op: 'storey.delete', guid: fromGuid });
  return ops;
}

/** The selected walls, rooms and slabs moved to storey `toId` (openings follow their wall). */
export function reassignOps(model: GeometriModel, guids: readonly string[], toId: number): StoreyResult {
  const target = byId(model, toId);
  if (!target) return { error: 'Etagen findes ikke længere' };
  const ops: ModelEditOp[] = [];
  let movable = 0;
  for (const g of guids) {
    const f = findElement(model, g);
    if (!f || f.kind === 'opening') continue;
    if (f.kind === 'slab' && f.row.source === undefined) continue;
    movable += 1;
    if (f.row.storey_id === toId) continue;
    ops.push({ op: `${f.kind}.update`, guid: g, set: { storey_id: toId } });
  }
  if (movable === 0) return { error: 'Vælg vægge, rum eller dæk — åbninger følger deres væg' };
  if (ops.length === 0) return { error: `Det valgte står allerede på ${target.name}` };
  const what = ops.length === 1 ? '1 element' : `${ops.length} elementer`;
  return { ops, label: `Flyt ${what} til ${target.name}` };
}

/**
 * Split a storey at world height `z` (at least 10 cm from its floor and
 * ceiling): a new storey `newGuid` from `z` to the old ceiling, holding
 * what stands at or above `z` (ruxd `storey.split`); `name` empty = the
 * server's "<name> (2)".
 */
export function splitStoreyOps(model: GeometriModel, guid: string, z: number, newGuid: string, name: string): StoreyResult {
  const s = byGuid(model, guid);
  if (!s) return { error: 'Etagen findes ikke længere' };
  const lo = round(s.z_min + SPLIT_MARGIN);
  const hi = round(s.z_max - SPLIT_MARGIN);
  if (!Number.isFinite(z) || z < lo || z > hi) return { error: `Delingen skal ligge mellem ${levelText(lo)} og ${levelText(hi)}` };
  const n = name.trim();
  return {
    ops: [{ op: 'storey.split', guid, z: round(z), new_guid: newGuid, ...(n ? { name: n } : {}) }],
    label: `Del ${s.name} ved ${levelText(z)}`,
  };
}

/** Merge storey `b` into `a` (neighbours: idx one apart); `a` spans both and keeps its name. */
export function mergeStoreyOps(model: GeometriModel, a: string, b: string): StoreyResult {
  const sa = byGuid(model, a);
  const sb = byGuid(model, b);
  if (!sa || !sb || sa === sb) return { error: 'Vælg to forskellige etager' };
  if (Math.abs(sa.id - sb.id) !== 1) return { error: 'Kun to etager lige over hinanden kan flettes' };
  return { ops: [{ op: 'storey.merge', guids: [a, b] }], label: `Flet ${sa.name} og ${sb.name}` };
}

/** Mark (or unmark) the ground storey; the server clears the mark on any other. Null when nothing changes. */
export function groundOps(model: GeometriModel, guid: string, on: boolean): ModelEditOp[] | null {
  const s = byGuid(model, guid);
  if (!s || (s.is_ground === true) === on) return null;
  return [{ op: 'storey.update', guid, set: { is_ground: on } }];
}
